// Admin authentication utilities using Firebase custom claims
import { getFunctions, httpsCallable } from 'firebase/functions';

/**
 * Check if a user has admin privileges via their ID token claims.
 * @param {Object} user - Firebase user object
 * @returns {Promise<boolean>} - True if the user is an admin
 */
export const checkAdminClaim = async (user) => {
  if (!user) return false;

  try {
    // Force token refresh to get latest claims
    const tokenResult = await user.getIdTokenResult(true);
    return tokenResult.claims.admin === true;
  } catch (error) {
    console.error('Error checking admin claim:', error);
    return false;
  }
};

/**
 * Set admin claim on a user (callable only by existing admins or during initial setup).
 * @param {string} targetEmail - Email of user to make admin
 * @returns {Promise<Object>} - Result of the operation
 */
export const setAdminClaim = async (targetEmail) => {
  const functions = getFunctions();
  const setAdmin = httpsCallable(functions, 'setAdminClaim');

  try {
    const result = await setAdmin({ targetEmail });
    return result.data;
  } catch (error) {
    console.error('Error setting admin claim:', error);
    throw error;
  }
};

/**
 * Remove admin claim from a user (callable only by existing admins).
 * @param {string} targetEmail - Email of user to remove admin from
 * @returns {Promise<Object>} - Result of the operation
 */
export const removeAdminClaim = async (targetEmail) => {
  const functions = getFunctions();
  const removeAdmin = httpsCallable(functions, 'removeAdminClaim');

  try {
    const result = await removeAdmin({ targetEmail });
    return result.data;
  } catch (error) {
    console.error('Error removing admin claim:', error);
    throw error;
  }
};

export default checkAdminClaim;
